import React from "react";
import { Box, Text, IconButton, Tooltip, Avatar, Icon } from "@chakra-ui/react";
import { MdDeleteOutline, MdAdminPanelSettings } from "react-icons/md";
import p_logo from "assets/img/logo.png";
import { useAdminPalette } from "./adminPalette";

/**
 * Resolves the tooltip text based on admin rights and connected user permissions.
 * @param {Object} admin - Admin user object with right field
 * @param {number} userConnectedRight - Connected user's right level
 * @returns {string} Tooltip text to display
 */
function getTooltipTitle(admin, userConnectedRight) {
    if (admin.right === 2) return "Impossible de supprimer un Super Admin";
    if (userConnectedRight === 1) return "Vous n'avez pas les droits";
    return "Retirer les droits admin";
}

/**
 * Renders the admin user info section with avatar, name badge, and email.
 * @param {Object} props - Component props
 * @param {Object} props.admin - Admin user object (name, email, photo, right)
 * @param {boolean} props.isMobile - Mobile viewport flag
 */
function AdminInfo({ admin, isMobile }) {
    const palette = useAdminPalette();
    return (
        <Box display="flex" gap={3} alignItems="center" flex={1} minW={0}>
            <Avatar src={admin.photo || p_logo} boxSize={isMobile ? "38px" : "42px"}
                border="2px solid" borderColor={palette.border} flexShrink={0} />
            <Box display="flex" alignItems="flex-start" flexDirection="column" gap={0.5} flex={1} minW={0}>
                <Text color={palette.textPrimary} fontWeight={600}
                    fontSize={isMobile ? "0.85rem" : "0.9rem"} overflow="hidden"
                    textOverflow="ellipsis" whiteSpace="nowrap" maxW="100%">
                    {admin.name}
                    {admin.right === 2 && (
                        <Tooltip label="Super Admin" hasArrow>
                            <Box as="span" display="inline-flex" verticalAlign="middle">
                                <Icon as={MdAdminPanelSettings} ml="2px" color="orange.500"
                                    boxSize={isMobile ? "14px" : "16px"} flexShrink={0} />
                            </Box>
                        </Tooltip>
                    )}
                </Text>
                <Text color={palette.textMuted} fontSize={isMobile ? "0.72rem" : "0.78rem"}
                    overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxW="100%">
                    {admin.email}
                </Text>
            </Box>
        </Box>
    );
}

/**
 * Renders the remove admin button with appropriate tooltip and disabled state.
 * @param {Object} props - Component props
 * @param {boolean} props.canRemove - Whether removal is allowed
 * @param {string} props.tooltipTitle - Tooltip text
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @param {Function} props.onClick - Click handler
 */
function RemoveButton({ canRemove, tooltipTitle, loading, isMobile, onClick }) {
    const palette = useAdminPalette();
    return (
        <Tooltip label={tooltipTitle} hasArrow>
            <Box as="span">
                <IconButton aria-label="Retirer les droits admin" size="sm" variant="outline" colorScheme="red"
                    isDisabled={!canRemove || loading} onClick={onClick}
                    icon={<Icon as={MdDeleteOutline} boxSize={isMobile ? "16px" : "18px"} />}
                    minW="auto" h="auto" p={isMobile ? "5px" : "7px"} flexShrink={0}
                    _disabled={{ color: palette.textMuted, borderColor: palette.border, opacity: 0.5, cursor: "not-allowed" }} />
            </Box>
        </Tooltip>
    );
}

/**
 * Renders a single admin member row with avatar, name, email, and remove button.
 * @param {Object} props
 * @param {Object} props.admin - Admin user object (id, name, email, photo, right)
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @param {number} props.userConnectedRight - Connected user's right level
 * @param {boolean} props.loading - Whether an action is in progress
 * @param {Function} props.onRemove - Handler called with admin object to remove
 */
function AdminMemberRow({ admin, isMobile, userConnectedRight, loading, onRemove }) {
    const palette = useAdminPalette();
    const canRemove = admin.right !== 2 && userConnectedRight !== 1;
    const tooltipTitle = getTooltipTitle(admin, userConnectedRight);

    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}
            padding={isMobile ? "10px" : "12px"} background={palette.rowBg}
            borderRadius="10px" border="1px solid" borderColor={palette.border} transition="all 0.2s ease"
            _hover={{ background: palette.rowBgHover }}>
            <AdminInfo admin={admin} isMobile={isMobile} />
            <RemoveButton canRemove={canRemove} tooltipTitle={tooltipTitle} loading={loading}
                isMobile={isMobile} onClick={() => onRemove(admin)} />
        </Box>
    );
}

export default AdminMemberRow;
